import { useEffect, useRef, useState } from 'react';
import { CoreGlobalFooter } from '@care/navigation';
import { GetServerSideProps } from 'next';
import { Grid, Box, useMediaQuery, Container, Theme } from '@mui/material';
import { Divider } from '@care/react-component-lib';
import Head from 'next/head';
import getConfig from 'next/config';
import JobIntro from '@/components/Jobs/JobIntro';
import JobDescription from '@/components/Jobs/JobDescription';
import JobAdditionalDetails from '@/components/Jobs/JobAdditionalDetails';
import JobPreferences from '@/components/Jobs/JobPreferences';
import JobSchedule from '@/components/Jobs/JobSchedule';
import NearbyJobs from '@/components/Jobs/NearbyJobs';
import SeoConversionBox from '@/components/SeoConversionBox';
import getJobDetailData from '@/components/pages/segmentationDetails/job/jobDetailPageProps';
import {
  AmplitudeEvent,
  setDeviceDataFromHeaders,
  setPagePropertiesFromData,
  trackAmplitudeEvent,
  HeaderData,
} from '@/lib/analyticsHelper';
import {
  ScreenName,
  ScreenTemplate,
  SeoPageId,
  TealiumSlot,
  SERVICE_ID_FOR_JOB_DETAILS,
  LaunchDarklyEvaluationReasons,
  UI_FEATURE_FLAGS,
  HTL_SLOTS,
} from '@/constants';
import mapJobPreferences from '@/utils/mapJobPreferences';
import mapJobSchedule from '@/utils/mapJobSchedule';
import generateEnrollmentUrl from '@/utils/generateEnrollmentUrl';
import SeoGlobalNavbar from '@/components/SeoGlobalNavbar';
import Breadcrumbs from '@/components/Breadcrumbs';
import { useLDClientFlags } from '@/components/hooks/useLDClientFlags';
import { GAMAds } from '@/components/GAMAds';
import { getSegmentationJobDetails } from '../../../../__generated__/getSegmentationJobDetails';

const {
  publicRuntimeConfig: { NEXT_PUBLIC_APP_URL },
} = getConfig();

interface PageProps {
  data: getSegmentationJobDetails;
  serviceShortName: string;
  testName: string;
  deviceChar?: HeaderData;
  serviceId?: string;
}

const JobDetailPage = ({ data, serviceShortName, testName, deviceChar }: PageProps) => {
  const { SEO_3487_GAM_ADS_SEO_MFE: GAMFlag } = useLDClientFlags();
  const showGAMAds = GAMFlag?.variationIndex === 1;
  const gamRef = useRef<HTMLDivElement | null>(null);
  const [isGamVisible, setIsGamVisible] = useState<boolean>(false);

  useEffect(() => {
    const trackPageViewAndTestExposureEvents = async () => {
      if (testName) {
        const amplitudeDataForTestExposure = {
          test_name: testName,
          test_variant: '1',
          service_short_name: serviceShortName,
        };

        await trackAmplitudeEvent(AmplitudeEvent.testExposure, amplitudeDataForTestExposure);
      }
      await trackAmplitudeEvent(AmplitudeEvent.jobViewed);
    };
    trackPageViewAndTestExposureEvents();
  }, [serviceShortName, testName]);

  useEffect(() => {
    if (
      isGamVisible &&
      GAMFlag &&
      GAMFlag.reason.kind === LaunchDarklyEvaluationReasons.RULE_MATCH
    ) {
      trackAmplitudeEvent(AmplitudeEvent.testExposure, {
        test_name: UI_FEATURE_FLAGS.SEO_3487_GAM_ADS_SEO_MFE.id,
        test_variant: GAMFlag.variationIndex,
        service_short_name: serviceShortName,
      });
    }
  }, [GAMFlag, isGamVisible, serviceShortName]);

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const showJobAdditionalDetails =
    serviceShortName === SERVICE_ID_FOR_JOB_DETAILS.childcare ||
    serviceShortName === SERVICE_ID_FOR_JOB_DETAILS.petcare ||
    serviceShortName === SERVICE_ID_FOR_JOB_DETAILS.housekeeping ||
    serviceShortName === SERVICE_ID_FOR_JOB_DETAILS.tutoring;

  const logoPath = `${NEXT_PUBLIC_APP_URL}/img/care-no-tag.jpg`;
  const { getSEOSegmentationJobDetails } = data;
  const { seoSegmentationJob } = getSEOSegmentationJobDetails || {};
  const breadcrumbs = data?.getSEOSegmentationBreadcrumbs.breadcrumbs || [];
  const { pageMetadata } = getSEOSegmentationJobDetails || {};
  const schemaLdJson = getSEOSegmentationJobDetails?.googleRichCardStructuredJson;
  const stringifyDeviceChar = JSON.stringify(deviceChar);

  useEffect(() => {
    const pageProperties = {
      screen_name: ScreenName.JOB,
      screen_template: ScreenTemplate.JOB,
      seo_page_id: SeoPageId.JOB,
      slots: TealiumSlot.VISITOR,
    };
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setPagePropertiesFromData(pageProperties);
      setDeviceDataFromHeaders(deviceChar);
    }
  }, [stringifyDeviceChar, deviceChar]);

  const caregiverPreferences = seoSegmentationJob?.jobPreferences
    ? mapJobPreferences(seoSegmentationJob.jobPreferences)
    : [];

  const jobSchedule = seoSegmentationJob?.schedule
    ? mapJobSchedule(seoSegmentationJob.schedule)
    : null;

  const enrollmentURL = generateEnrollmentUrl({
    serviceIdForMember: seoSegmentationJob?.serviceIdForMember || '',
    serviceId: seoSegmentationJob?.serviceId || '',
    isMobile,
  });

  const jobSchemaLdJson = () => {
    return schemaLdJson;
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsGamVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.5,
      }
    );

    const currentGamRef = gamRef.current;

    if (currentGamRef) {
      observer.observe(currentGamRef);
    }

    return () => {
      if (currentGamRef) {
        observer.unobserve(currentGamRef);
      }
    };
  }, [gamRef, isMobile]);

  return (
    <Box>
      <Head>
        <title>{pageMetadata?.title}</title>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="description" content={pageMetadata?.metaDescriptions} />
        <meta name="keywords" content={pageMetadata?.metaKeywords} />
        {pageMetadata?.indexable && <link rel="canonical" href={pageMetadata?.canonicalURL} />}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="verify-v1" content="zz15lR180u9eCqTfur/9l+bMP75AampYtDEm6OnbkWY=" />
        <meta property="og:title" content={pageMetadata?.title} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={logoPath} />
        <meta property="og:url" content={pageMetadata?.canonicalURL} />
        <meta property="og:description" content={pageMetadata?.metaDescriptions} />
        <meta property="og:site_name" content="Care.com" />
        <meta content="image/jpeg" property="og:image:type" />
        <meta content="1080" property="og:image:width" />
        <meta content="1080" property="og:image:height" />
        <link href="/img/care-no-tag.jpg" />
        <meta property="fb:page_id" content="6030284861" />
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'http://schema.org',
              '@type': 'Organization',
              logo: logoPath,
              name: 'Care.com',
              sameAs: [
                'https://www.facebook.com/caredotcom/',
                'https://twitter.com/CareDotCom',
                'https://www.instagram.com/careorg/',
                'https://www.linkedin.com/company/care-com',
              ],
              url: 'https://www.care.com',
            }),
          }}
        />
      </Head>
      <SeoGlobalNavbar enrollmentURL={enrollmentURL} />
      {!isMobile && (
        <Container maxWidth="md">
          <Box mb={4} mt={4}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </Box>
        </Container>
      )}

      <JobIntro
        title={seoSegmentationJob?.job.title || ''}
        hiResImageURL={seoSegmentationJob?.seekerAvatar ?? undefined}
        payRange={seoSegmentationJob?.job.hourlyRateRange || null}
        city={seoSegmentationJob?.city || ''}
        state={seoSegmentationJob?.state || ''}
        startDate={seoSegmentationJob?.job.startDate || ''}
        joinNowURL={enrollmentURL}
        type={seoSegmentationJob?.type || null}
      />

      <Container maxWidth="md" disableGutters>
        <Divider sx={{ pl: { md: 3 } }} />
      </Container>
      <JobDescription profileDesc={seoSegmentationJob?.description || ''} />
      {showGAMAds && (
        <Box ref={gamRef}>
          <GAMAds slotName={HTL_SLOTS.SEO_JOB_PROFILE_INLINE_1} />
        </Box>
      )}
      {showJobAdditionalDetails && seoSegmentationJob?.jobPreferences && (
        <>
          <Container maxWidth="md" disableGutters>
            <Divider sx={{ pl: { md: 3 } }} />
          </Container>

          <JobAdditionalDetails
            jobPreferences={seoSegmentationJob?.jobPreferences}
            serviceName={serviceShortName}
          />
        </>
      )}

      {caregiverPreferences.length > 0 && (
        <>
          <Container maxWidth="md" disableGutters>
            <Divider sx={{ pl: { md: 3 } }} />
          </Container>

          <JobPreferences jobPreferences={caregiverPreferences} serviceName={serviceShortName} />
        </>
      )}
      <Container maxWidth="md" disableGutters>
        <Divider sx={{ pl: { md: 3 } }} />
      </Container>
      {jobSchedule && (
        <>
          <JobSchedule {...jobSchedule} />
          <Container maxWidth="md" disableGutters>
            <Divider sx={{ pl: { md: 3 } }} />
          </Container>
        </>
      )}
      {showGAMAds && (
        <Box>
          <GAMAds slotName={HTL_SLOTS.SEO_JOB_PROFILE_INLINE_2} />
        </Box>
      )}
      <NearbyJobs jobs={getSEOSegmentationJobDetails?.nearbyJobs || []} />
      <Grid item xs={12} mt={{ xs: 8, md: 10 }} mb={{ md: 8 }}>
        <SeoConversionBox
          header="When do you want a job?"
          enrollmentURL={enrollmentURL}
          serviceId={seoSegmentationJob?.serviceId || ''}
        />
      </Grid>

      {isMobile && (
        <Container maxWidth="md">
          <Box mb={4} mt={4}>
            <Breadcrumbs breadcrumbs={breadcrumbs} />
          </Box>
        </Container>
      )}

      {schemaLdJson && (
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: jobSchemaLdJson() ?? '' }}
        />
      )}
      <CoreGlobalFooter />
    </Box>
  );
};

export default JobDetailPage;

export const getServerSideProps: GetServerSideProps = async (context) => {
  return getJobDetailData(context);
};
